import { v4 as uuidv4 } from 'uuid';
import { usernameFromFHIR4HumanName } from './general';
import {
  CallbackNumber,
  EntryResource,
  Modality,
  VisitType,
} from '../features/get-care/Components/select-service-type/types';
import { AccountUser, Member, MemberPlanElement } from '../features/account/types';
import { CommonDropdownType } from '../types';
import { getVisitFee } from '../features/get-care/utils/getVisitFeeUtils';

export enum NotificationTypeSocketCommand {
  VISIT = 'Visit',
}

// TODO: Move to visits types
export enum VisitSocketCommand {
  Arrived = 'arrived',
  Waiting = 'waiting',
  Triaged = 'triaged',
  JoinMeeting = 'joinMeeting',
  InProgress = 'inProgress',
  OnLeave = 'onleave',
  LeaveMeeting = 'leaveMeeting',
  EndMeeting = 'endMeeting',
  Finished = 'finished',
  Cancelled = 'cancelled',
  VisitList = 'visitList',
}

export enum VisitStatus {
  // * Booking statuses (Mobile phone user):
  Planned = 'planned',
  Booking = 'booking',
  Intake = 'intake',
  // Waiting Room statuses:
  Arrived = 'arrived',
  Waiting = 'waiting',
  InProgress = 'in-progress',
  Triaged = 'triaged',
  Unknown = 'unknown',
  OnLeave = 'onleave',
  // EndMeeting = 'end-meeting',
  // Completed statuses:
  Finished = 'finished',
  Cancelled = 'cancelled',
  EnteredInError = 'entered-in-error',
  Incomplete = 'incomplete',
  NoShow = 'no-show',
}

export type NotificationActionMessage = {
  reqType: string;
  reqId: string;
  messagingParams: {
    command: string;
    notireq: {
      type: NotificationTypeSocketCommand;
      key: any;
    };
  };
};

export type VisitActionMessage = {
  reqType: string;
  reqId: string;
  visitParams: {
    command: VisitSocketCommand;
    visit?: {
      reference: string;
      type: string;
    };
    actor?: {
      reference: string;
      type: string;
      display?: string;
    };
    patient?: {
      reference: string;
      type: string;
      display?: string;
    };
    for: {
      reference: string;
      type: string;
      display: string;
    };
    serviceType: {
      reference: string;
      type: string;
    };
    visitType: {
      visitType: string;
      modality: Modality;
      visitFee: string;
    };
    modality: Modality;
    chiefComplaint?: {
      reference: string;
      type: string;
    };
    callbackNumber?: {
      reference: string;
      type: string;
    };
    state: {
      code: string;
      display: string;
      system: string;
    };
    selectedPlan?: { reference: string; type: string; display: string; organizationName: string };
    visitStatus?: VisitStatus;
    cancellationReason?: {
      reason: string;
      additionalDetails?: string;
      cancelled_by?: string;
    };
  };
};

type CreateVisitActionMessageParams = {
  command: VisitSocketCommand;
  visitId?: string;
  actor?: any;
  patient?: AccountUser | Member | null;
  visitStatus?: VisitStatus;
  reason?: string;
  additionalDetails?: string;
  serviceType?: EntryResource | null;
  visitType?: VisitType | null;
  chiefComplaint?: CallbackNumber;
  callbackNumber?: CallbackNumber;
  state?: CommonDropdownType | null;
  selectedPlan?: MemberPlanElement | null;
};

export function createVisitActionMessage({
  command,
  visitId,
  actor,
  patient,
  visitStatus,
  reason,
  additionalDetails,
  serviceType,
  visitType,
  chiefComplaint,
  callbackNumber,
  state,
  selectedPlan,
}: CreateVisitActionMessageParams): VisitActionMessage {
  const visitActionData = {
    reqType: 'visit',
    reqId: uuidv4(),
    visitParams: {
      command: command,
    },
  } as VisitActionMessage;

  if (visitId) {
    visitActionData.visitParams.visit = {
      reference: visitId,
      type: 'Visit',
    };
  }

  if (visitStatus) {
    visitActionData.visitParams.visitStatus = visitStatus;
  }

  if (actor) {
    visitActionData.visitParams.actor = {
      reference: actor.id,
      type: actor.resourceType,
      display: actor?.name ? usernameFromFHIR4HumanName(actor.name[0]) : '',
    };
  }

  if (patient) {
    visitActionData.visitParams.patient = {
      reference: patient?.id,
      type: 'patient',
      display: patient?.name ? usernameFromFHIR4HumanName(patient.name[0]) : '',
    };
    visitActionData.visitParams.patient = {
      reference: patient?.id,
      type: 'Patient',
      display: patient?.name ? usernameFromFHIR4HumanName(patient.name[0]) : '',
    };
  }

  if (serviceType) {
    visitActionData.visitParams.serviceType = {
      reference: serviceType.id,
      type: serviceType.resourceType,
    };
  }

  if (visitType) {
    visitActionData.visitParams.visitType = {
      visitType: visitType?.visitType,
      modality: visitType.modality,
      visitFee: getVisitFee({
        visitPlan: selectedPlan,
        visitType: visitType,
        visitServiceType: serviceType,
      }).toString(),
    };
    visitActionData.visitParams.modality = visitType.modality;
  }

  if (chiefComplaint) {
    visitActionData.visitParams.chiefComplaint = {
      reference: chiefComplaint?.reference ?? '',
      type: 'QuestionnaireResponse',
    };
  }

  if (callbackNumber) {
    visitActionData.visitParams.callbackNumber = {
      reference: callbackNumber?.reference ?? '',
      type: 'QuestionnaireResponse',
    };
  }

  if (state) {
    visitActionData.visitParams.state = {
      system: 'https://www.usps.com/',
      code: state?.value ?? '',
      display: state?.label ?? '',
    };
  }

  if (selectedPlan) {
    visitActionData.visitParams.selectedPlan = {
      reference: selectedPlan?.id,
      type: 'Plan',
      display: selectedPlan?.name,
      organizationName: selectedPlan?.organization?.display,
    };
  }

  if (reason) {
    visitActionData.visitParams.cancellationReason = {
      reason,
      additionalDetails,
      cancelled_by: (actor?.id ? actor.id : '').toString(),
    };
  }

  return visitActionData;
}

export function createNotificationActionMessage(
  notificationType: NotificationTypeSocketCommand,
  reference?: string,
): NotificationActionMessage {
  return {
    reqType: 'messaging',
    reqId: uuidv4(),
    messagingParams: {
      command: 'notiSubscribe',
      notireq: {
        type: notificationType,
        key: [
          {
            type: 'Patient',
            reference: reference,
          },
        ],
      },
    },
  };
}
